import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { PackageSearch, Loader2, Truck } from "lucide-react";
import { API } from "../context/CartContext";

const STATUS_COLORS = {
  pending: "bg-orange-100 text-orange-800",
  awaiting_transfer: "bg-yellow-100 text-yellow-800",
  paid: "bg-green-100 text-green-800",
  shipped: "bg-blue-100 text-blue-800",
};

export default function OrderTrackingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    axios.get(`${API}/orders/${orderId}`)
      .then((r) => { setOrder(r.data); setState("ok"); })
      .catch(() => setState("notfound"));
  }, [orderId]);

  return (
    <div className="min-h-screen bg-brand-cream flex items-center justify-center px-6">
      <div data-testid="order-tracking-card" className="bg-white rounded-3xl p-10 max-w-md w-full border border-brand-green/15 shadow-xl">
        {state === "loading" && <Loader2 size={48} className="mx-auto text-brand-orange animate-spin" />}
        {state === "notfound" && (
          <div className="text-center">
            <PackageSearch size={56} className="mx-auto text-brand-brown/40" />
            <p data-testid="order-not-found" className="mt-4 font-bold text-brand-brown">{t("track.notFound")}</p>
          </div>
        )}
        {state === "ok" && order && (
          <>
            <h1 className="font-heading text-4xl text-brand-dark uppercase">{t("track.title")}</h1>
            <div className="mt-6 space-y-4">
              <div className="flex justify-between items-center text-sm">
                <span className="font-semibold text-brand-brown">{t("track.orderId")}</span>
                <span className="font-mono font-bold text-brand-dark text-xs">{order.order_id}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="font-semibold text-brand-brown">{t("track.status")}</span>
                <span data-testid="tracking-status"
                  className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${STATUS_COLORS[order.status] || "bg-gray-100 text-gray-600"}`}>
                  {t(`track.statuses.${order.status}`, { defaultValue: order.status })}
                </span>
              </div>
              {order.tracking_number && (
                <div className="flex items-center justify-between gap-3 bg-brand-cream rounded-2xl px-5 py-4">
                  <span className="flex items-center gap-2 text-sm font-semibold text-brand-brown">
                    <Truck size={18} className="text-brand-green" /> {t("track.resi")}
                  </span>
                  <span data-testid="tracking-number" className="font-mono font-bold text-brand-dark">{order.tracking_number}</span>
                </div>
              )}
              <div className="border-t border-brand-green/15 pt-4 space-y-2">
                {order.items?.map((it, i) => (
                  <div key={i} className="flex justify-between text-sm text-brand-brown">
                    <span>Agrodyke {it.weight_kg} kg × {it.quantity}</span>
                  </div>
                ))}
                <div className="flex justify-between font-bold text-brand-dark">
                  <span>Total</span>
                  <span>{order.currency === "USD" ? `$${Number(order.total_usd).toFixed(2)}` : `Rp ${Number(order.total_idr).toLocaleString("id-ID")}`}</span>
                </div>
              </div>
            </div>
          </>
        )}
        <button data-testid="tracking-home-button" onClick={() => navigate("/")}
          className="mt-8 w-full px-8 py-3 rounded-full bg-brand-orange text-white font-bold text-sm uppercase tracking-wide hover:bg-brand-dark transition-colors">
          {t("payment.home")}
        </button>
      </div>
    </div>
  );
}
